<div class="overflow-hidden">
  <!-- Hero -->
  <section class="relative min-h-screen flex items-center bg-secondary pt-20">
    <div class="absolute inset-0 overflow-hidden pointer-events-none max-md:hidden" aria-hidden="true">
      <div class="absolute top-1/4 start-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px]"></div>
      <div class="absolute bottom-1/4 end-1/4 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[120px]"></div>
    </div>

    <div class="container mx-auto px-4 relative z-10">
      <div class="grid lg:grid-cols-2 gap-16 items-center">
        <div class="text-center lg:text-start">
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-8 leading-tight animate-fade-in">
            {{ t.hero.title }}
          </h1>
          <p class="text-xl md:text-2xl text-muted-foreground mb-10 animate-fade-in-up" style="animation-delay: 0.2s">
            {{ t.hero.subtitle }}
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up" style="animation-delay: 0.4s">
            <a routerLink="/register" class="thi-btn-primary-xl group">
              {{ t.hero.ctaPrimary }}
              <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon"></i>
            </a>
            <a routerLink="/support" class="thi-btn-outline-xl">{{ t.hero.ctaSecondary }}</a>
          </div>
        </div>
        <div class="animate-fade-in-up" style="animation-delay: 0.6s">
          <img
            src="assets/landing-thi/business-hero-480w.png"
            srcset="
              assets/landing-thi/business-hero-480w.png 480w,
              assets/landing-thi/business-hero.png 1024w
            "
            sizes="(max-width: 1024px) 100vw, min(50vw, 640px)"
            width="1024"
            height="1024"
            decoding="async"
            fetchpriority="high"
            alt=""
            class="rounded-2xl shadow-lg md:shadow-2xl w-full h-auto"
          />
        </div>
      </div>
    </div>
  </section>

  <!-- Social proof -->
  <section class="py-16 bg-secondary/50 border-y border-border">
    <div class="container mx-auto px-4">
      <div class="grid grid-cols-3 gap-8 max-w-3xl mx-auto text-center">
        <div>
          <p class="text-3xl md:text-4xl font-bold text-primary mb-2">1,000+</p>
          <p class="text-muted-foreground">{{ t.socialProof.businesses }}</p>
        </div>
        <div>
          <p class="text-3xl md:text-4xl font-bold text-primary mb-2">50,000+</p>
          <p class="text-muted-foreground">{{ t.socialProof.invoices }}</p>
        </div>
        <div>
          <p class="text-3xl md:text-4xl font-bold text-primary mb-2">10,000+</p>
          <p class="text-muted-foreground">{{ t.socialProof.saved }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Product -->
  <section class="py-24 bg-background">
    <div class="container mx-auto px-4">
      <div class="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
        <div>
          <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-6">{{ t.product.title }}</h2>
          <p class="text-lg text-muted-foreground mb-8">{{ t.product.whatIs }}</p>
          <div class="space-y-6">
            <div class="flex items-start gap-4">
              <div class="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <i class="fa fa-users text-primary"></i>
              </div>
              <div>
                <h3 class="font-semibold text-foreground">{{ t.product.forWhom }}</h3>
                <p class="text-muted-foreground">{{ t.product.forWhomDesc }}</p>
              </div>
            </div>
            <div class="flex items-start gap-4">
              <div class="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <i class="fa fa-magic text-primary"></i>
              </div>
              <div>
                <h3 class="font-semibold text-foreground">{{ t.product.howHelps }}</h3>
                <p class="text-muted-foreground">{{ t.product.howHelpsDesc }}</p>
              </div>
            </div>
          </div>
          <div class="mt-8">
            <a routerLink="/register" class="thi-btn-primary-lg group inline-flex">
              {{ t.hero.ctaPrimary }}
              <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon"></i>
            </a>
          </div>
        </div>
        <div class="rounded-2xl overflow-hidden shadow-lg md:shadow-2xl border border-border">
          <img
            src="assets/landing-thi/dashboard-mockup-480w.png"
            srcset="
              assets/landing-thi/dashboard-mockup-480w.png 480w,
              assets/landing-thi/dashboard-mockup-960w.png 960w,
              assets/landing-thi/dashboard-mockup.png 1920w
            "
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 90vw, 672px"
            width="1920"
            height="1080"
            loading="lazy"
            decoding="async"
            alt=""
            class="w-full h-auto"
          />
        </div>
      </div>
    </div>
  </section>

  <!-- Benefits -->
  <section class="py-24 bg-secondary/30">
    <div class="container mx-auto px-4">
      <div class="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
        <div
          *ngFor="let benefit of benefits"
          class="bg-card p-8 rounded-2xl border border-border hover:shadow-xl hover:border-primary/20 transition-shadow duration-200 group"
        >
          <div class="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary transition-[background-color] duration-200">
            <i class="text-2xl text-primary group-hover:text-primary-foreground" [ngClass]="benefit.icon"></i>
          </div>
          <h3 class="text-xl font-bold text-foreground mb-3">{{ benefit.title }}</h3>
          <p class="text-muted-foreground">{{ benefit.desc }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- AI — 2×2 card grid matching Lovable -->
  <section class="py-24 thi-section-gradient-soft relative overflow-hidden">
    <div class="container mx-auto px-4 relative z-10">
      <div class="max-w-4xl mx-auto text-center mb-16">
        <div class="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-8">
          <i class="fa fa-magic" aria-hidden="true"></i>
          <span class="text-sm font-medium">{{ aiBadgeLabel }}</span>
        </div>
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">{{ t.ai.title }}</h2>
        <p class="text-lg text-muted-foreground max-w-2xl mx-auto">{{ t.ai.step2Desc }}</p>
      </div>

      <!-- Explicit layout (scoped SCSS) so global `.grid` rules never collapse this to one column -->
      <div class="thi-ai-split">
        <div class="order-2 min-w-0 md:order-1">
          <div class="thi-ai-step-grid">
            <div
              *ngFor="let step of aiSteps; let i = index"
              class="bg-card border border-border rounded-xl p-6 hover:shadow-lg hover:border-accent/30 transition-shadow duration-200"
            >
              <div class="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                <i class="text-accent" [ngClass]="step.icon"></i>
              </div>
              <div class="text-2xl font-bold text-accent mb-2">{{ i + 1 }}</div>
              <h3 class="font-semibold text-foreground mb-2">{{ step.title }}</h3>
              <p class="text-sm text-muted-foreground">{{ step.desc }}</p>
            </div>
          </div>
        </div>
        <div class="order-1 thi-ai-split__illustration md:order-2">
          <img
            src="assets/landing-thi/ai-receipt-scan-400w.png"
            srcset="
              assets/landing-thi/ai-receipt-scan-400w.png 400w,
              assets/landing-thi/ai-receipt-scan.png 1024w
            "
            sizes="(max-width: 768px) 90vw, 28rem"
            width="1024"
            height="1024"
            loading="lazy"
            decoding="async"
            alt=""
          />
        </div>
      </div>

      <div class="max-w-2xl mx-auto mt-16 text-center">
        <div class="bg-card border border-border rounded-2xl p-8">
          <div class="w-12 h-12 bg-success/10 rounded-xl flex items-center justify-center mx-auto mb-4">
            <i class="fa fa-shield text-2xl text-success"></i>
          </div>
          <h3 class="text-xl font-bold text-foreground mb-3">{{ t.ai.accuracy }}</h3>
          <p class="text-muted-foreground">{{ t.ai.accuracyDesc }}</p>
        </div>
        <a routerLink="/register" class="thi-btn-primary-lg group inline-flex mt-8">
          {{ t.hero.ctaPrimary }}
          <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon"></i>
        </a>
      </div>
    </div>
  </section>

  <!-- Features -->
  <section class="py-24 bg-background">
    <div class="container mx-auto px-4">
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4">{{ t.features.title }}</h2>
      </div>
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
        <div
          *ngFor="let feature of features"
          class="bg-card p-8 rounded-2xl border border-border hover:shadow-xl hover:border-primary/20 transition-shadow duration-200 group"
        >
          <div class="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary transition-[background-color] duration-200">
            <i class="text-2xl text-primary group-hover:text-primary-foreground" [ngClass]="feature.icon"></i>
          </div>
          <h3 class="text-xl font-bold text-foreground mb-3">{{ feature.title }}</h3>
          <p class="text-muted-foreground">{{ feature.desc }}</p>
        </div>
      </div>
      <div class="text-center mt-12">
        <a routerLink="/register" class="thi-btn-primary-lg group inline-flex">
          {{ t.hero.ctaPrimary }}
          <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon"></i>
        </a>
      </div>
    </div>
  </section>

  <!-- Use cases -->
  <section class="py-24 bg-secondary/30">
    <div class="container mx-auto px-4">
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4">{{ t.useCases.title }}</h2>
      </div>

      <div class="flex flex-wrap justify-center gap-4 mb-12">
        <button
          type="button"
          *ngFor="let uc of useCases; let index = index"
          (click)="activeUseCase = index"
          class="flex items-center gap-3 px-6 py-4 rounded-xl transition-[background-color,box-shadow,border-color] duration-200 border border-border"
          [ngClass]="
            activeUseCase === index
              ? 'bg-primary text-primary-foreground shadow-lg border-transparent'
              : 'bg-card text-foreground hover:border-primary/30'
          "
        >
          <i [ngClass]="uc.icon"></i>
          <span class="font-medium">{{ uc.title }}</span>
        </button>
      </div>

      <div class="max-w-4xl mx-auto">
        <div class="bg-card border border-border rounded-2xl p-8 md:p-12">
          <div class="grid md:grid-cols-3 gap-6">
            <div class="bg-destructive/5 border border-destructive/20 rounded-xl p-6">
              <h3 class="font-bold text-destructive mb-3">{{ t.useCases.problem }}</h3>
              <p class="text-foreground text-sm">{{ useCases[activeUseCase].problem }}</p>
            </div>
            <!-- Solution uses accent colour, matching Lovable -->
            <div class="bg-accent/5 border border-accent/20 rounded-xl p-6">
              <h3 class="font-bold text-accent mb-3">{{ t.useCases.solution }}</h3>
              <p class="text-foreground text-sm">{{ useCases[activeUseCase].solution }}</p>
            </div>
            <div class="bg-success/5 border border-success/20 rounded-xl p-6">
              <h3 class="font-bold text-success mb-3">{{ t.useCases.benefit }}</h3>
              <p class="text-foreground text-sm">{{ useCases[activeUseCase].benefit }}</p>
            </div>
          </div>
          <div class="mt-8 text-center">
            <a routerLink="/register" class="thi-btn-primary-lg group inline-flex">
              {{ t.hero.ctaPrimary }}
              <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Testimonials -->
  <section class="py-24 bg-background">
    <div class="container mx-auto px-4">
      <h2 class="text-3xl md:text-4xl font-bold text-foreground text-center mb-16">{{ t.testimonials.title }}</h2>
      <div class="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
        <div *ngFor="let testimonial of testimonials" class="bg-card p-8 rounded-2xl border border-border">
          <blockquote class="text-lg mb-6 text-foreground">"{{ testimonial.quote }}"</blockquote>
          <div>
            <p class="font-semibold text-foreground">{{ testimonial.author }}</p>
            <p class="text-sm text-muted-foreground">{{ testimonial.role }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- FAQ -->
  <section id="faq" class="py-24 bg-secondary/30">
    <div class="container mx-auto px-4">
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4">{{ t.faq.title }}</h2>
        <p class="text-lg text-muted-foreground">{{ t.faq.subtitle }}</p>
      </div>
      <div class="max-w-3xl mx-auto space-y-4">
        <div *ngFor="let faq of faqs; let i = index" class="bg-card border border-border rounded-xl px-6">
          <button
            type="button"
            (click)="toggleFaq(i)"
            class="thi-faq-trigger w-full text-start py-4 text-foreground font-semibold flex justify-between items-center gap-4 hover:no-underline"
          >
            {{ faq.q }}
            <i class="fa fa-chevron-down transition-transform flex-shrink-0" [class.rotate-180]="openFaqIndex === i"></i>
          </button>
          <div *ngIf="openFaqIndex === i" class="pb-4 text-muted-foreground border-t border-border/50 pt-3">
            {{ faq.a }}
          </div>
        </div>
      </div>
      <div class="text-center mt-12">
        <a routerLink="/support" class="thi-btn-outline inline-flex">{{ moreFaqLabel }}</a>
      </div>
    </div>
  </section>

  <!-- Final CTA — gradient matches Lovable (to-accent/10) -->
  <section class="py-24 thi-section-gradient-cta relative overflow-hidden">
    <div
      class="absolute inset-0 z-[1] flex items-center justify-center pointer-events-none max-md:hidden"
      aria-hidden="true"
    >
      <div class="w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px]"></div>
    </div>
    <div class="container mx-auto px-4 relative z-10">
      <div class="max-w-3xl mx-auto text-center">
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">{{ t.cta.ready }}</h2>
        <p class="text-lg md:text-xl text-muted-foreground mb-10">{{ t.cta.readyDesc }}</p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <a routerLink="/register" class="thi-btn-primary-xl group">
            {{ t.hero.ctaPrimary }}
            <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon"></i>
          </a>
          <a routerLink="/support" class="thi-btn-outline-xl">{{ t.hero.ctaSecondary }}</a>
        </div>
      </div>
    </div>
  </section>
</div>
